// ============================================================
// School Service (Mock)
// ============================================================

import { mockSchools, mockSchoolSummaries } from '@/mock/schools';
import { MOCK_DELAY } from '@/constants/app';

import { organizationApi } from '@/api/super-admin/organization.api';

const delay = (ms = MOCK_DELAY.MEDIUM) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const schoolService = {
  getSchools: async (organizationId) => {
    if (organizationId) {
      try {
        const schools = await organizationApi.getSchools(organizationId);
        const list = Array.isArray(schools) ? schools : schools.items || [];
        // Ensure name exists for switcher
        const mappedSchools = list.map(s => ({
          ...s,
          name: s.school_name || s.name
        }));
        return { data: mappedSchools };
      } catch (error) {
        console.error('Failed to fetch schools from API:', error);
        throw error;
      }
    }
    await delay();
    return { data: [...mockSchools] };
  },

  getSchoolById: async (id) => {
    await delay();
    const school = mockSchools.find((s) => s.id === id);
    if (!school) throw new Error('School not found');
    return { data: { ...school } };
  },

  getSchoolsByUser: async (schoolIds) => {
    await delay();
    if (!schoolIds || schoolIds.length === 0) return { data: [] };
    const schools = mockSchools.filter((s) => schoolIds.includes(s.id));
    return { data: [...schools] };
  },

  searchSchools: async (query, filters) => {
    await delay();
    let results = [...mockSchools];

    if (query) {
      const lowerQuery = query.toLowerCase();
      results = results.filter(
        (s) =>
          s.name.toLowerCase().includes(lowerQuery) ||
          s.code.toLowerCase().includes(lowerQuery) ||
          s.principalName.toLowerCase().includes(lowerQuery) ||
          s.city.toLowerCase().includes(lowerQuery)
      );
    }

    if (filters) {
      if (filters.status && filters.status !== 'all') {
        results = results.filter((s) => s.status === filters.status);
      }
      if (filters.schoolType && filters.schoolType !== 'all') {
        results = results.filter((s) => s.schoolType === filters.schoolType);
      }
      if (filters.city && filters.city !== 'all') {
        results = results.filter((s) => s.city === filters.city);
      }
    }
    return { data: results };
  },

  updateSchool: async (schoolId, data) => {
    await delay();
    const index = mockSchools.findIndex((s) => s.id === schoolId);
    if (index !== -1) {
      mockSchools[index] = { ...mockSchools[index], ...data, lastUpdated: new Date().toISOString() };
      return { data: { ...mockSchools[index] } };
    }
    throw new Error('School not found');
  },

  getSchoolSummary: async (schoolId) => {
    await delay();
    const summary = mockSchoolSummaries[schoolId];
    if (summary) {
      return { data: { ...summary } };
    }
    throw new Error('School summary not found');
  },

  compareSchools: async (schoolIds, allSchools = []) => {
    await delay();
    if (!Array.isArray(schoolIds) || schoolIds.length === 0) {
      return { data: [] };
    }

    const comparisonData = schoolIds.map((id) => {
      let school = mockSchools.find((s) => s.id === id);
      if (!school && Array.isArray(allSchools)) {
        school = allSchools.find((s) => s.id === id || s.school_id === id);
      }
      if (!school) {
        school = {
          id,
          name: 'School Branch',
          city: 'City',
          studentCount: 350,
        };
      }

      const normalizedSchool = {
        ...school,
        id: school.id || id,
        name: school.name || school.school_name || 'School Branch',
        city: school.city || school.location || 'Branch',
        studentCount: school.studentCount ?? school.student_count ?? school.total_students ?? 350,
      };

      const summary = mockSchoolSummaries[id] || {
        todaysCollection: 45000,
        thisMonthCollection: 1250000,
        pendingFees: 350000,
        collectionPercentage: 85,
        outstandingBalance: 350000,
        todaysPresent: 320,
        todaysAbsent: 15,
        attendancePercentage: 95.5,
        newAdmissions: 25,
        transfers: 5,
        inactiveStudents: 2,
        averageAttendance: 95.2,
        monthlyAttendanceTrend: [92, 94, 95, 93, 96, 95, 94]
      };

      return {
        school: normalizedSchool,
        summary,
      };
    });

    return { data: comparisonData };
  }
};

export default schoolService;
